import React, { useEffect } from 'react';
import { Box, Typography, Link } from '@mui/material';
import { useLocation } from 'react-router-dom';
import DocNavigation from '../components/DocNavigation';
import { CopyBlock } from 'react-code-blocks';
import { dracula } from 'react-code-blocks';

const ProfileApiDiscovery: React.FC = React.memo(() => {
	const location = useLocation();

	useEffect(() => {
		window.scrollTo({
			top: 0,
			behavior: 'smooth',
		});
	}, [location.pathname]);

	return (
		<Box sx={{ paddingLeft: 4 }}>
			<Typography
				sx={{
					fontWeight: 'bold',
					fontSize: { xs: '1.2rem', sm: '1.5rem' },
					color: '#0b3452',
					display: 'inline-block',
					marginBottom: 0,
					lineHeight: 1.5,
				}}
			>
				Profile API Discovery
			</Typography>

			<Typography
				variant="subtitle1"
				sx={{ mt: 4, color: '#0b3452', fontWeight: 'bold' }}
			>
				Legal Framework
			</Typography>

			<Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
				This endpoint is intended to be used by Financial Information Users{' '}
				<Box component="span" fontWeight="bold">
					(FIUs)
				</Box>{' '}
				to gain the insight of whether a user, who has consented to share their
				mobile number with the account aggregator{' '}
				<Box component="span" fontWeight="bold">
					(AA) , has already registered with the account aggregator.
				</Box>{' '}
				Additionally, the endpoint also gives other user profile data such as{' '}
				<Box component="span" fontWeight="bold">
					whether a user has linked accounts
				</Box>
				with the account aggregator.
			</Typography>

			<Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
				The usage of this endpoint{' '}
				<Box component="span" fontWeight="bold">
					assumes that the legal framework for explicitly taking the
				</Box>
				consent of the customer for sharing the mobile number with the account
				aggregator for the mentioned use case{' '}
				<Box component="span" fontWeight="bold">
					is already in place.{' '}
				</Box>
				Finvu will not be storing any personally identifiable information (PII)
				such as the mobile number of the user.
			</Typography>

			<Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
				Here, the legal framework means a Terms of Service (provided by Finvu)
				already agreed upon by the FIU for using the endpoint.
			</Typography>

			<Typography
				variant="subtitle1"
				sx={{ mt: 4, color: '#0b3452', fontWeight: 'bold' }}
			>
				UI Design Guidelines
			</Typography>

			<Box
				sx={{
					mt: 2,
					mb: 2,
					display: { xs: 'block', md: 'flex' },
					width: '100%',
					maxWidth: '700px',
					justifyContent: 'center',
					alignItems: 'flex-start',
					margin: '0 auto',
					border: { xs: 'none', md: '1px solid black' },
					borderRadius: 2,
					overflow: 'hidden',
				}}
			>
				{/* Left Column */}
				<Box
					sx={{
						width: { xs: '100%', md: '50%' },
						borderRight: { xs: 'none', md: '1px solid black' },
						p: 2,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						boxSizing: 'border-box',
					}}
				>
					<Box
						sx={{
							width: '100%',
							textAlign: 'center',
							borderBottom: { xs: 'none', md: '1px solid black' },
							pb: 1,
							mb: 2,
						}}
					>
						<Typography>
							Design for{' '}
							<Box component="span" fontWeight="bold">
								deemed
							</Box>{' '}
							consent
						</Typography>
					</Box>
					<Box
						component="img"
						src="/images/profileDiscovery1.png"
						alt="Profile Discovery Image"
						sx={{
							width: '100%',
							maxWidth: '300px',
							height: { xs: '300px', md: '500px' },
							objectFit: 'contain',
							display: 'block',
							mx: 'auto',
						}}
					/>
				</Box>
				{/* Right Column */}
				<Box
					sx={{
						width: { xs: '100%', md: '50%' },
						p: 2,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						boxSizing: 'border-box',
					}}
				>
					<Box
						sx={{
							width: '100%',
							textAlign: 'center',
							borderBottom: { xs: 'none', md: '1px solid black' },
							pb: 1,
							mb: 2,
						}}
					>
						<Typography>
							Design for{' '}
							<Box component="span" fontWeight="bold">
								explicit
							</Box>{' '}
							consent
						</Typography>
					</Box>
					<Box
						component="img"
						src="/images/profileDiscovery2.png"
						alt="Profile Discovery Image"
						sx={{
							width: '100%',
							maxWidth: '300px',
							height: { xs: '300px', md: '500px' },
							objectFit: 'contain',
							display: 'block',
							mx: 'auto',
						}}
					/>
				</Box>
			</Box>
			<Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
				These designs reflect the aforementioned legal opinion.
			</Typography>

			<Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
				<Box component="span" sx={{ textDecoration: 'underline' }}>
					Deemed Consent
				</Box>
				: Itemized description of the consent taken from user, including profile
				details
			</Typography>

			<Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
				<Box component="span" sx={{ textDecoration: 'underline' }}>
					Explicit Consent (Recommended)
				</Box>
				: Explicit consent for fetching profile details of the user
			</Typography>
			{/* How it Works Section */}
			<Typography
				variant="subtitle1"
				sx={{ mt: 4, color: '#0b3452', fontWeight: 'bold' }}
			>
				Endpoint URL
			</Typography>
			<Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
				Endpoint is a POST method REST API with the below metioned URL:
			</Typography>
			<ul>
				<li>
					<strong>UAT:</strong>{' '}
					<code>https://aauat.finvu.in/API/v2/user-profile</code>
				</li>
				<li>
					<strong>Production:</strong>{' '}
					<code>https://aalive.finvu.in/API/v2/user-profile</code>
				</li>
			</ul>

			<Typography
				variant="subtitle1"
				sx={{ mt: 4, color: '#0b3452', fontWeight: 'bold' }}
			>
				Authentication
			</Typography>
			{/* Technical Notes */}
			<Typography sx={{ mt: 1, lineHeight: 1.7 }}>
				The authentication mechanism for FIUs will be the same as the
				authentication mechanism used for accessing the core ReBIT-schema based
				APIs.
			</Typography>

			<Typography sx={{ mt: 1, lineHeight: 1.7 }}>
				FIUs will need to pass the following{' '}
				<Box component="span" fontWeight="bold">
					headers
				</Box>{' '}
				in the request for it to be authenticated correctly:
			</Typography>

			<Box sx={{ mt: 2, mb: 4 }}>
				<ul>
					<li>
						<Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
							<Box component="span" fontWeight="bold">
								client_api_key:
							</Box>{' '}
							This is the API key of the FIU for which the profile of the user
							wants to be retrieved.
						</Typography>
					</li>

					<li>
						<Typography sx={{ mt: 1, color: '#555', lineHeight: 1.7 }}>
							<Box component="span" fontWeight="bold">
								x-jws-signature:
							</Box>{' '}
							This is the detached JWS signature of the request body.
						</Typography>
					</li>
				</ul>
			</Box>

			<Typography
				sx={{ mt: 2, color: '#555', lineHeight: 1.7, fontWeight: 'bold' }}
			>
				On top of this, only those FIUs for which Terms of Service have been
				agreed upon will be allowed to access this endpoint. Please reach out to{' '}
				<Link href="mailto:support@cookiejar.co.in" sx={{ color: '#0b3452' }}>
					support@cookiejar.co.in
				</Link>{' '}
				for enabling it for your entity.
			</Typography>

			<Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
				Additionally, since this is part of the core AA APIs,{' '}
				<Box component="span" fontWeight="bold">
					only whitelisted IPs on the firewall can access this endpoint in
					production.{' '}
				</Box>
			</Typography>

			<Typography
				variant="subtitle1"
				sx={{ mt: 4, color: '#0b3452', fontWeight: 'bold' }}
			>
				Request Structure
			</Typography>

			<Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
				Below is a{' '}
				<Box component="span" fontWeight="bold">
					sample request payload
				</Box>{' '}
				for the Profile API:
			</Typography>
			<CopyBlock
				text={`{
    "ver": "2.0.0",
    "timestamp": "2025-01-23T05:45:48.313+00:00",
    "txnid": "795038d3-86fb-4d3a-a681-2d39e8f4fc3c",
    "mobileNum": "9988776600",
    "consentProvided": true,
    "userConsentId": "455038d3-86fb-4d3a-a681-2d39e8f4fc7d"
}`}
				language="json"
				showLineNumbers={true}
				theme={dracula}
			/>
			<Box sx={{ mt: 2, mb: 4 }}>
				<ul>
					<li>
						<Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
							<Box component="span" fontWeight="bold">
								version:
							</Box>{' '}
							Version of the endpoint
						</Typography>
					</li>

					<li>
						<Typography sx={{ mt: 1, color: '#555', lineHeight: 1.7 }}>
							<Box component="span" fontWeight="bold">
								timestamp:
							</Box>{' '}
							Useful for logging and debugging
						</Typography>
					</li>

					<li>
						<Typography sx={{ mt: 1, color: '#555', lineHeight: 1.7 }}>
							<Box component="span" fontWeight="bold">
								txnid:
							</Box>{' '}
							Unique identifier for the request to the endpoint
						</Typography>
					</li>

					<li>
						<Typography sx={{ mt: 1, color: '#555', lineHeight: 1.7 }}>
							<Box component="span" fontWeight="bold">
								mobileNumber:
							</Box>{' '}
							Identifier used to check user profile details
						</Typography>
					</li>

					<li>
						<Typography sx={{ mt: 1, color: '#555', lineHeight: 1.7 }}>
							<Box component="span" fontWeight="bold">
								consentProvided:
							</Box>{' '}
							Boolean to indicate whether user has consented to the use of this
							endpoint
						</Typography>
					</li>

					<li>
						<Typography sx={{ mt: 1, color: '#555', lineHeight: 1.7 }}>
							<Box component="span" fontWeight="bold">
								userConsentId:
							</Box>{' '}
							Unique identifier to associate the user consent
						</Typography>
					</li>
				</ul>
			</Box>

			<Typography
				variant="subtitle1"
				sx={{ mt: 4, color: '#0b3452', fontWeight: 'bold' }}
			>
				Response Structure
			</Typography>

			<Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
				Below is a{' '}
				<Box component="span" fontWeight="bold">
					sample response payload
				</Box>{' '}
				for the Profile API:
			</Typography>
			<CopyBlock
				text={`{
    "ver": "1.0.0",
    "timestamp": "2025-01-23T05:45:48.313+00:00",
    "txnid": "795038d3-86fb-4d3a-a681-2d39e8f4fc3c",
    "profile": {
        "registered": true,
        "accountInfo":{
            "hasLinkedAccounts": true,
        }
    }
}`}
				language="json"
				showLineNumbers={true}
				theme={dracula}
			/>

			<Box sx={{ mt: 2, mb: 4 }}>
				<ul>
					<li>
						<Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
							<Box component="span" fontWeight="bold">
								version:
							</Box>{' '}
							Version of the endpoint
						</Typography>
					</li>

					<li>
						<Typography sx={{ mt: 1, color: '#555', lineHeight: 1.7 }}>
							<Box component="span" fontWeight="bold">
								timestamp:
							</Box>{' '}
							Useful for logging and debugging
						</Typography>
					</li>

					<li>
						<Typography sx={{ mt: 1, color: '#555', lineHeight: 1.7 }}>
							<Box component="span" fontWeight="bold">
								txnid:
							</Box>{' '}
							Unique identifier for the request to the endpoint
						</Typography>
					</li>

					<li>
						<Typography sx={{ mt: 1, color: '#555', lineHeight: 1.7 }}>
							<Box component="span" fontWeight="bold">
								profile:
							</Box>{' '}
							Profile details of the user
						</Typography>
					</li>

					<li>
						<Typography sx={{ mt: 1, color: '#555', lineHeight: 1.7 }}>
							<Box component="span" fontWeight="bold">
								consentProvided:
							</Box>{' '}
							Boolean to indicate whether user has consented to the use of this
							endpoint
							<ul>
								<li>
									<Typography sx={{ mt: 1, color: '#555', lineHeight: 1.7 }}>
										<Box component="span" fontWeight="bold">
											registered:
										</Box>{' '}
										Gives the registration status of the user
									</Typography>
								</li>

								<li>
									<Typography sx={{ mt: 1, color: '#555', lineHeight: 1.7 }}>
										<Box component="span" fontWeight="bold">
											accountInfo:
										</Box>{' '}
										Gives information on user's linked accounts
										<ul>
											<li>
												<Typography
													sx={{ mt: 1, color: '#555', lineHeight: 1.7 }}
												>
													<Box component="span" fontWeight="bold">
														hasLinkedAccounts:
													</Box>{' '}
													Boolean that gives the information whether a user has
													accounts linked with Finvu
												</Typography>
											</li>
										</ul>
									</Typography>
								</li>
							</ul>
						</Typography>
					</li>
				</ul>
			</Box>

			<DocNavigation currentPath={location.pathname} />
		</Box>
	);
});

export default ProfileApiDiscovery;
